import React, { useEffect, useRef, useMemo, useCallback } from 'react'
import cn from 'classnames'
import styles from './babylon-viewer.module.scss'
import {
    contentTypeToModelExtension,
    modelExtensionFromArrayBuffer,
    modelLoadConfig,
    modelMimeTypeForExtension,
} from './babylonModelLoadConfig'
// @ts-expect-error - Vite ?url import suffix not recognised by TypeScript
import environmentSpecularUrl from '../../../public/babylon/environmentSpecular.env?url'
import type {
    Engine as BabylonEngine,
    WebGPUEngine as BabylonWebGPUEngine,
    Scene as BabylonScene,
    ArcRotateCamera as BabylonArcRotateCamera,
    AbstractMesh,
    Skeleton,
} from '@babylonjs/core'
import { isMobile } from 'lib/device'

declare global {
    interface Window {
        logBabylon?: boolean
    }
}

type FileType = {
    name: string
    url: string
    type: string
    size: number
    id?: string
    content_type?: string
    contentType?: string
    filename?: string
    original_filename?: string
    mime?: string
    preview_url?: string
}

type PreviewAspect = '1x1' | '16x9' | '9x16'

interface BabylonViewerProps {
    file: FileType
    height?: number | string
    className?: string
    onPreviewGenerated?: (blob: Blob, dataUrl: string) => void | Promise<void>
    onPreviewFailed?: (error: unknown) => void
    onPreviewProgress?: (percent: number) => void
    capturePreview?: boolean
    captureAspects?: PreviewAspect[]
    preferLocalUrl?: string
    captureResolution?: number
}

// Type alias for the dynamically-imported @babylonjs/core namespace
type BabylonNamespace = typeof import('@babylonjs/core')

const loadBabylonModules = async () => {
    // Use top-level loader import to avoid subpath dynamic import issues in dev
    const tryLoad = async () => {
        const [BABYLON, loaders] = await Promise.all([
            import('@babylonjs/core'),
            import('@babylonjs/loaders'),
        ])
        // The GLTF loader creates animation groups for animated assets and expects
        // Scene.prototype.beginDirectAnimation. Apply the extension from the core
        // namespace so dev does not issue a late optimized-dep request on click.
        if (typeof BABYLON.Scene.prototype.beginDirectAnimation !== 'function') {
            BABYLON.AddAnimationExtensions(BABYLON.Scene, BABYLON.Bone)
        }
        const screenshotTools = await import('@babylonjs/core/Misc/screenshotTools')
        await import('@babylonjs/core/Materials/Textures/Loaders/envTextureLoader')
        return { BABYLON, loaders, screenshotTools }
    }
    try {
        return await tryLoad()
    } catch (e) {
        console.warn('First attempt to load Babylon modules failed, retrying...', e)
        await new Promise(r => setTimeout(r, 300))
        return await tryLoad()
    }
}

interface LoadingScreenElement extends HTMLDivElement {
    updateProgress?: (progress: number) => void
}

const loadModel = async (
    _gltfModule: unknown,
    file: FileType,
    preferUrl?: string,
): Promise<LoadedModelSource | null> => {
    const primaryUrl = preferUrl || file.url
    const { isGLB, pluginExtension } = modelLoadConfig(file)
    let generatedUrl: string | null = null

    const dispose = () => {
        if (generatedUrl) {
            URL.revokeObjectURL(generatedUrl)
            generatedUrl = null
        }
    }

    try {
        if (isGLB) {
            if (primaryUrl.startsWith('blob:') || primaryUrl.startsWith('data:')) {
                return {
                    source: primaryUrl,
                    pluginExtension: '.glb',
                    dispose,
                }
            }

            const loadedBinary = await loadBinaryFile({ url: primaryUrl })
            if (!loadedBinary) {
                dispose()
                return null
            }

            const blob = new Blob([loadedBinary.arrayBuffer], { type: 'model/gltf-binary' })
            generatedUrl = URL.createObjectURL(blob)
            return {
                source: generatedUrl,
                pluginExtension: '.glb',
                dispose,
            }
        }

        if (!pluginExtension) {
            const loadedBinary = await loadBinaryFile({ url: primaryUrl }, 2, 250)
            const inferredExtension =
                contentTypeToModelExtension(loadedBinary?.contentType) ||
                (loadedBinary ? modelExtensionFromArrayBuffer(loadedBinary.arrayBuffer) : '')

            if (loadedBinary && inferredExtension) {
                const blob = new Blob([loadedBinary.arrayBuffer], {
                    type: modelMimeTypeForExtension(inferredExtension),
                })
                generatedUrl = URL.createObjectURL(blob)
                return {
                    source: generatedUrl,
                    pluginExtension: `.${inferredExtension}`,
                    dispose,
                }
            }
        }

        return {
            source: primaryUrl,
            pluginExtension,
            dispose,
        }
    } catch (error) {
        console.error('Error loading 3D model:', error)
        dispose()
        return null
    }
}

type LoadedModelSource = {
    source: string
    pluginExtension?: string
    dispose?: () => void
}

type LoadedBinaryFile = {
    arrayBuffer: ArrayBuffer
    contentType: string
}

const setupEngine = async (
    BABYLON: BabylonNamespace,
    canvas: HTMLCanvasElement,
    options?: { disableWebGPU?: boolean },
) => {
    let useWebGPU = false
    // @ts-expect-error - forceBabylonWebGL is a custom debug flag on window
    const forceWebGL = Boolean(options?.disableWebGPU || window?.forceBabylonWebGL)

    // Check for WebGPU support and device
    try {
        if (!forceWebGL && navigator.gpu) {
            const adapter = await navigator.gpu.requestAdapter()
            if (adapter) {
                const device = await adapter.requestDevice()
                if (device) {
                    useWebGPU = true
                }
            }
        }
    } catch (e) {
        console.warn('WebGPU not available, falling back to WebGL:', e)
    }

    if (window.logBabylon) {
        console.log('Using WebGPU:', useWebGPU)
    }
    const antialias = useWebGPU ? true : false

    let engine: BabylonEngine | BabylonWebGPUEngine

    // Babylon.js engine constructors accept options like disableLoadingScreen
    // that are not reflected in the published type definitions, so we spread
    // the extra options separately to avoid "unknown property" TS errors.
    const webGPUOpts = {
        enableAllFeatures: true,
        setMaximumLimits: true,
        antialias,
        useHighPrecisionMatrix: true,
        disableLoadingScreen: true,
        adaptToDeviceRatio: true,
        powerPreference: 'high-performance' as const,
    }
    const webGLOpts = {
        useHighPrecisionMatrix: true,
        premultipliedAlpha: false,
        preserveDrawingBuffer: true,
        antialias,
        forceSRGBBufferSupportState: false,
        disableLoadingScreen: true,
        adaptToDeviceRatio: true,
        powerPreference: 'high-performance' as const,
    }

    try {
        if (useWebGPU) {
            engine = new BABYLON.WebGPUEngine(canvas, webGPUOpts)
            await (engine as BabylonWebGPUEngine).initAsync()
        } else {
            engine = new BABYLON.Engine(canvas, antialias, webGLOpts)
        }
    } catch (e) {
        console.warn('Failed to initialize WebGPU engine, falling back to WebGL:', e)
        engine = new BABYLON.Engine(canvas, antialias, webGLOpts)
    }
    return engine
}

const getSceneComplexity = (scene: BabylonScene) => {
    let totalVertices = 0
    let totalIndices = 0

    if (!scene?.meshes) {
        return { totalVertices, totalIndices }
    }

    scene.meshes.forEach((mesh: AbstractMesh) => {
        try {
            if (mesh && typeof mesh.getTotalVertices === 'function') {
                totalVertices += mesh.getTotalVertices() || 0
            }
            if (mesh && typeof mesh.getIndices === 'function') {
                const indices = mesh.getIndices()
                if (indices && indices.length) {
                    totalIndices += indices.length
                }
            }
        } catch (error) {
            if (window.logBabylon) {
                console.warn('Failed to inspect mesh for complexity metrics', error)
            }
        }
    })

    return { totalVertices, totalIndices }
}

const setupScene = async (
    BABYLON: BabylonNamespace,
    canvas: HTMLCanvasElement,
    options?: { disableWebGPU?: boolean },
) => {
    if (!canvas) {
        throw new Error('Canvas is not available')
    }

    const engine = await setupEngine(BABYLON, canvas, options)

    // Ensure engine is properly initialized before creating scene
    if (!engine || engine.isDisposed) {
        throw new Error('Failed to initialize engine')
    }

    // Wait a frame to ensure engine is ready
    await new Promise(resolve => setTimeout(resolve, 0))

    const scene = new BABYLON.Scene(engine)
    if (!scene) {
        throw new Error('Failed to create scene')
    }

    scene.getEngine().hideLoadingUI()
    BABYLON.SceneLoader.ShowLoadingScreen = false

    scene.skipPointerMovePicking = true
    // @ts-expect-error - useGeometryIdsMap exists at runtime but missing from @babylonjs/core types
    scene.useGeometryIdsMap = true
    // @ts-expect-error - useClonedMeshMap is read-only in types but assignable at init time
    scene.useClonedMeshMap = true

    scene.clearColor = new BABYLON.Color4(0, 0, 0, 0)
    scene.autoClear = true
    scene.autoClearDepthAndStencil = true
    scene.blockMaterialDirtyMechanism = false
    scene.performancePriority = BABYLON.ScenePerformancePriority.BackwardCompatible
    scene.fogEnabled = false
    scene.particlesEnabled = false
    scene.collisionsEnabled = false
    scene.probesEnabled = false
    scene.lightsEnabled = true
    scene.skipFrustumClipping = true

    scene.pointerMovePredicate = () => false
    scene.cameraToUseForPointers = null

    return { engine, scene }
}

const setupCamera = (
    BABYLON: BabylonNamespace,
    scene: BabylonScene,
    canvas: HTMLCanvasElement,
    options?: {
        worldExtends?: {
            min: InstanceType<BabylonNamespace['Vector3']>
            max: InstanceType<BabylonNamespace['Vector3']>
        }
        assetExtension?: string
    },
) => {
    let camera = scene.activeCamera as BabylonArcRotateCamera | null

    if (!camera || !(camera instanceof BABYLON.ArcRotateCamera)) {
        scene.createDefaultCamera(true)
        camera = scene.activeCamera as BabylonArcRotateCamera | null
    }

    if (!camera || !(camera instanceof BABYLON.ArcRotateCamera)) {
        camera = new BABYLON.ArcRotateCamera(
            'camera',
            -Math.PI / 2,
            Math.PI / 2,
            1,
            BABYLON.Vector3.Zero(),
            scene,
        )
        scene.activeCamera = camera
    }

    camera.attachControl(canvas, true)

    camera.useFramingBehavior = true
    // getBehaviorByName returns Behavior<Node> which lacks FramingBehavior-specific props
    const framingBehavior = camera.getBehaviorByName('Framing') as
        | (ReturnType<BabylonArcRotateCamera['getBehaviorByName']> & {
              framingTime?: number
              elevationReturnTime?: number
              zoomOnBoundingInfo?: (min: unknown, max: unknown) => void
          })
        | null
    if (framingBehavior) {
        framingBehavior.framingTime = 0
        framingBehavior.elevationReturnTime = -1
    }

    if (options?.assetExtension && ['.glb', '.gltf', '.obj'].includes(options.assetExtension)) {
        camera.alpha += Math.PI
    }
    camera.beta = Math.PI / 2

    camera.useAutoRotationBehavior = false

    if (options?.worldExtends) {
        camera.lowerRadiusLimit = null

        if (framingBehavior) {
            framingBehavior.zoomOnBoundingInfo(options.worldExtends.min, options.worldExtends.max)
        }

        const worldSize = options.worldExtends.max.subtract(options.worldExtends.min)
        const radius = Math.max(camera.radius || worldSize.length(), 0.001)
        camera.upperRadiusLimit = 5 * radius
        camera.pinchPrecision = 200 / radius
    }

    camera.wheelDeltaPercentage = 0.01
    camera.pinchDeltaPercentage = 0.01

    return camera
}

const setupEnvironment = async (
    BABYLON: BabylonNamespace,
    scene: BabylonScene,
    options?: { preferLowRes?: boolean },
) => {
    if (scene.isDisposed || scene.environmentTexture) return

    const preferLowRes = options?.preferLowRes

    const baseUrl =
        // @ts-expect-error - Vite injects import.meta.env at build time; TS doesn't know about it
        (typeof import.meta !== 'undefined' && import.meta?.env?.BASE_URL) || '/'
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl

    const highResCandidates = [
        {
            requestUrl: environmentSpecularUrl,
            allowFallbackOnError: false,
        },
        {
            requestUrl: 'https://assets.babylonjs.com/environments/environmentSpecular.env',
            allowFallbackOnError: true,
        },
        {
            requestUrl: 'https://assets.babylonjs.com/environments/ulmerMuenster.env',
            allowFallbackOnError: true,
        },
    ]

    const lowResCandidates = [
        {
            requestUrl: 'https://playground.babylonjs.com/textures/environment.env',
            allowFallbackOnError: true,
        },
        {
            requestUrl: `${normalizedBase}/babylon/environmentSpecular.env`,
            allowFallbackOnError: true,
        },
        {
            requestUrl: 'https://assets.babylonjs.com/environments/studio.env',
            allowFallbackOnError: true,
        },
    ]

    const candidates = preferLowRes ? lowResCandidates : highResCandidates

    await new Promise<void>(resolve => {
        const tryLoad = (index: number) => {
            if (index >= candidates.length) {
                console.warn('Unable to determine environment texture URL')
                resolve()
                return
            }

            const candidate = candidates[index]
            const envTexture = BABYLON.CubeTexture.CreateFromPrefilteredData(
                candidate.requestUrl,
                scene,
            )

            if (!envTexture) {
                if (candidate.allowFallbackOnError) {
                    tryLoad(index + 1)
                } else {
                    console.warn(
                        `Environment texture creation returned null for ${candidate.requestUrl}`,
                    )
                    resolve()
                }
                return
            }

            const applyEnvironment = () => {
                if (scene.isDisposed) {
                    envTexture.dispose()
                    resolve()
                    return
                }

                scene.environmentTexture = envTexture
                scene.environmentIntensity = preferLowRes ? 0.9 : 1.0

                const camera = scene.activeCamera
                const size =
                    camera && camera.maxZ && camera.minZ
                        ? Math.max((camera.maxZ - camera.minZ) / 2, 100)
                        : 1000

                const skybox = scene.createDefaultSkybox(envTexture, true, size, 0.3, false)
                if (skybox) {
                    skybox.isPickable = false
                    skybox.infiniteDistance = true
                }

                const imageProcessing = scene.imageProcessingConfiguration
                if (imageProcessing) {
                    imageProcessing.toneMappingEnabled = true
                    imageProcessing.toneMappingType =
                        BABYLON.ImageProcessingConfiguration.TONEMAPPING_KHR_PBR_NEUTRAL
                    imageProcessing.exposure = preferLowRes ? 0.95 : 1.0
                    imageProcessing.contrast = 1.1
                }

                resolve()
            }

            envTexture.rotationY = 5.54

            try {
                if (scene.isDisposed) {
                    envTexture.dispose()
                    resolve()
                } else if (typeof envTexture.isReady === 'function' && envTexture.isReady()) {
                    applyEnvironment()
                } else if (envTexture.onLoadObservable?.addOnce) {
                    envTexture.onLoadObservable.addOnce(applyEnvironment)
                } else {
                    setTimeout(applyEnvironment, 0)
                }
            } catch (error) {
                console.warn('Environment texture setup failed', error)
                tryLoad(index + 1)
            }

            // @ts-expect-error - onErrorObservable exists at runtime on CubeTexture but not in published types
            envTexture.onErrorObservable?.add(() => {
                if (scene.isDisposed) {
                    resolve()
                    return
                }
                envTexture.dispose()
                if (candidate.allowFallbackOnError) {
                    tryLoad(index + 1)
                } else {
                    resolve()
                }
            })
        }

        tryLoad(0)
    })
}

const setupLighting = (BABYLON: BabylonNamespace, scene: BabylonScene) => {
    const hemiLight = new BABYLON.HemisphericLight('hemiLight', new BABYLON.Vector3(0, 1, 0), scene)
    hemiLight.intensity = 0.7
    hemiLight.groundColor = new BABYLON.Color3(0.2, 0.2, 0.2)

    const dirLight = new BABYLON.DirectionalLight('dirLight', new BABYLON.Vector3(-1, -2, 1), scene)
    dirLight.position = new BABYLON.Vector3(20, 40, -20)
    dirLight.intensity = 0.7

    return { hemiLight, dirLight }
}

export default function BabylonViewer({
    file,
    height,
    className,
    onPreviewGenerated,
    onPreviewFailed,
    onPreviewProgress,
    capturePreview = false,
    captureAspects,
    preferLocalUrl,
    captureResolution,
}: BabylonViewerProps) {
    const IDLE_FRAME_INTERVAL = 1000 / 24
    const INTERACTION_TIMEOUT_MS = 2000
    const performanceProfile = useMemo(() => {
        if (typeof navigator === 'undefined') return 'medium'
        const cores = navigator.hardwareConcurrency || 4
        // @ts-expect-error - navigator.deviceMemory is a non-standard API (Device Memory API)
        const memory = navigator.deviceMemory || 4
        if (!isMobile && cores >= 8 && memory >= 8) return 'high'
        if (cores <= 4 || memory <= 4 || (isMobile && cores <= 6)) return 'low'
        return 'medium'
    }, [])

    const complexityThresholds = useMemo(() => {
        switch (performanceProfile) {
            case 'high':
                return { vertex: 260000, index: 520000 }
            case 'low':
                return { vertex: 120000, index: 260000 }
            default:
                return { vertex: 180000, index: 360000 }
        }
    }, [performanceProfile])

    useEffect(() => {
        if (window.logBabylon) {
            console.log('Performance profile', performanceProfile, complexityThresholds)
        }
    }, [performanceProfile, complexityThresholds])
    const canvasRef = useRef<HTMLCanvasElement>(null)
    // Engine and Scene refs use the library types intersected with runtime-only
    // properties accessed in this component (pinchData, isActiveMeshesFrozen, etc.)
    const engineRef = useRef<(BabylonEngine | BabylonWebGPUEngine) | null>(null)
    const sceneRef = useRef<
        | (BabylonScene & {
              pinchData?: { previousDistance: number; pinchStartRadius: number } | null
              isActiveMeshesFrozen?: boolean
              activeParticleSystems?: unknown[]
          })
        | null
    >(null)
    const observerRef = useRef<IntersectionObserver | null>(null)
    const loadingScreenRef = useRef<LoadingScreenElement | null>(null)
    const renderLoopActiveRef = useRef<boolean>(false)
    const previewCapturedRef = useRef<boolean>(false)
    const isUserInteractingRef = useRef(false)
    const interactionTimeoutRef = useRef<number | null>(null)
    const lastRenderTimeRef = useRef(0)
    const canFreezeActiveMeshesRef = useRef(false)
    const freezeTimeoutRef = useRef<number | null>(null)
    const clearInteractionTimeout = useCallback(() => {
        if (interactionTimeoutRef.current !== null) {
            clearTimeout(interactionTimeoutRef.current)
            interactionTimeoutRef.current = null
        }
    }, [])

    const clearFreezeTimeout = useCallback(() => {
        if (freezeTimeoutRef.current !== null) {
            clearTimeout(freezeTimeoutRef.current)
            freezeTimeoutRef.current = null
        }
    }, [])

    const scheduleFreezeActiveMeshes = useCallback(() => {
        clearFreezeTimeout()
        if (!canFreezeActiveMeshesRef.current) return
        const scene = sceneRef.current
        if (!scene || scene.isDisposed) return

        freezeTimeoutRef.current = window.setTimeout(() => {
            const activeScene = sceneRef.current
            if (!canFreezeActiveMeshesRef.current || !activeScene || activeScene.isDisposed) {
                return
            }
            try {
                activeScene.freezeActiveMeshes(false)
                if (window.logBabylon) {
                    console.log('Active meshes frozen for static scene')
                }
            } catch (error) {
                console.warn('Failed to freeze active meshes', error)
            }
        }, INTERACTION_TIMEOUT_MS)
    }, [clearFreezeTimeout, INTERACTION_TIMEOUT_MS])

    const markInteraction = useCallback(() => {
        isUserInteractingRef.current = true
        clearInteractionTimeout()
        clearFreezeTimeout()
        const scene = sceneRef.current
        if (scene?.isActiveMeshesFrozen) {
            try {
                scene.unfreezeActiveMeshes()
            } catch (error) {
                console.warn('Failed to unfreeze active meshes', error)
            }
        }
        interactionTimeoutRef.current = window.setTimeout(() => {
            isUserInteractingRef.current = false
            interactionTimeoutRef.current = null
            if (canFreezeActiveMeshesRef.current) {
                scheduleFreezeActiveMeshes()
            }
        }, INTERACTION_TIMEOUT_MS)
    }, [
        clearInteractionTimeout,
        clearFreezeTimeout,
        scheduleFreezeActiveMeshes,
        INTERACTION_TIMEOUT_MS,
    ])

    const clearAnimationState = useCallback(() => {
        const scene = sceneRef.current
        if (!scene || !scene.animationGroups) return
        scene.animationGroups.forEach(group => {
            if (group.isPlaying) {
                group.stop()
            }
        })
        try {
            if (scene.isActiveMeshesFrozen) {
                scene.unfreezeActiveMeshes()
            }
        } catch (error) {
            console.warn('Failed to unfreeze meshes while clearing animation state', error)
        }
        canFreezeActiveMeshesRef.current = false
        clearFreezeTimeout()
    }, [clearFreezeTimeout])

    // No preview logic here; 3DModelLoader decides if/when to show preview.

    // Memoize touch event handlers since they're complex calculations that don't need to change
    const handleTouchStart = useCallback(
        (e: TouchEvent) => {
            markInteraction()
            let touchPoints = Array.from(e.touches)
            if (touchPoints.length === 2) {
                const previousDistance = Math.hypot(
                    touchPoints[0].clientX - touchPoints[1].clientX,
                    touchPoints[0].clientY - touchPoints[1].clientY,
                )

                if (sceneRef.current?.activeCamera) {
                    const arcCamera = sceneRef.current.activeCamera as BabylonArcRotateCamera
                    const pinchStartRadius = arcCamera.radius
                    // Store these values in refs or state if needed across handlers
                    sceneRef.current.pinchData = { previousDistance, pinchStartRadius }
                }
            }
            e.preventDefault()
            e.stopPropagation()
        },
        [markInteraction],
    )

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            markInteraction()
            if (e.touches.length === 2 && sceneRef.current?.pinchData) {
                const currentTouches = Array.from(e.touches)
                const currentDistance = Math.hypot(
                    currentTouches[0].clientX - currentTouches[1].clientX,
                    currentTouches[0].clientY - currentTouches[1].clientY,
                )

                if (sceneRef.current?.activeCamera) {
                    const camera = sceneRef.current.activeCamera as BabylonArcRotateCamera
                    const zoomDelta =
                        (currentDistance - sceneRef.current.pinchData.previousDistance) * 0.01
                    const newRadius = camera.radius - zoomDelta

                    camera.radius = Math.min(
                        Math.max(newRadius, camera.lowerRadiusLimit),
                        camera.upperRadiusLimit,
                    )

                    sceneRef.current.pinchData.previousDistance = currentDistance
                }
            }
            e.preventDefault()
            e.stopPropagation()
        },
        [markInteraction],
    )

    const handleTouchEnd = useCallback(
        (e: TouchEvent) => {
            markInteraction()
            if (e.touches.length < 2 && sceneRef.current) {
                sceneRef.current.pinchData = null
            }
            e.preventDefault()
            e.stopPropagation()
        },
        [markInteraction],
    )

    const preventScroll = useCallback(
        (e: Event) => {
            markInteraction()
            e.preventDefault()
            e.stopPropagation()
        },
        [markInteraction],
    )

    const handlePointerMove = useCallback(
        (event: PointerEvent) => {
            if (event.buttons) {
                markInteraction()
            }
        },
        [markInteraction],
    )

    // Memoize the resize handler
    const handleResize = useCallback(() => {
        if (engineRef.current && !engineRef.current.isDisposed) {
            engineRef.current.resize()
        }
    }, [])

    // Memoize the loading screen setup since it's a complex DOM operation
    const setupLoadingScreenMemo = useMemo(() => {
        return (canvasRef: React.RefObject<HTMLCanvasElement>) => {
            const loadingScreen = document.createElement('div') as LoadingScreenElement
            loadingScreen.style.position = 'absolute'
            loadingScreen.style.top = '50%'
            loadingScreen.style.left = '50%'
            loadingScreen.style.transform = 'translate(-50%, -50%)'
            loadingScreen.style.color = '#ccc'
            loadingScreen.style.fontSize = '24px'
            loadingScreen.style.textAlign = 'center'
            loadingScreen.style.width = '100px'
            loadingScreen.style.height = '100px'
            loadingScreen.style.display = 'flex'
            loadingScreen.style.flexDirection = 'column'
            loadingScreen.style.alignItems = 'center'
            loadingScreen.style.justifyContent = 'center'

            const spinner = document.createElement('i')
            spinner.className = 'fa fa-circle-o-notch fa-spin fa-lg'
            spinner.style.position = 'relative'
            loadingScreen.appendChild(spinner)

            const progressText = document.createElement('div')
            progressText.style.position = 'absolute'
            progressText.style.left = '50%'
            progressText.style.bottom = '-30px'
            progressText.style.transform = 'translateX(-50%)'
            progressText.style.fontSize = '14px'
            progressText.style.color = '#fff'
            loadingScreen.appendChild(progressText)

            loadingScreen.updateProgress = (progress: number) => {
                if (progress >= 0) {
                    progressText.textContent = `${Math.round(progress)}%`
                } else {
                    progressText.textContent = ''
                }
            }

            canvasRef.current?.parentNode?.appendChild(loadingScreen)
            return loadingScreen
        }
    }, [])

    // Memoize engine and scene validity check
    const isEngineSceneValid = useCallback(() => {
        return (
            engineRef.current &&
            sceneRef.current &&
            !renderLoopActiveRef.current &&
            sceneRef.current.activeCamera &&
            !sceneRef.current.isDisposed &&
            !engineRef.current.isDisposed
        )
    }, [])

    // Memoize observer options
    const observerOptions = useMemo(
        () => ({
            root: null,
            threshold: 0.1,
        }),
        [],
    )

    const startRenderLoop = useCallback(() => {
        if (isEngineSceneValid()) {
            renderLoopActiveRef.current = true
            lastRenderTimeRef.current = 0
            engineRef.current.runRenderLoop(() => {
                const scene = sceneRef.current
                const engine = engineRef.current
                if (!scene || scene.isDisposed || !engine || engine.isDisposed) {
                    return
                }

                const now = performance.now()
                const hasActiveAnimations = Boolean(
                    scene.animationGroups?.some(group => group.isPlaying),
                )
                const hasActiveParticles = Boolean(scene.activeParticleSystems?.length)
                const shouldThrottle =
                    !hasActiveAnimations && !hasActiveParticles && !isUserInteractingRef.current

                if (shouldThrottle && now - lastRenderTimeRef.current < IDLE_FRAME_INTERVAL) {
                    return
                }

                lastRenderTimeRef.current = now
                scene.render()
            })
        }
    }, [isEngineSceneValid])

    const stopRenderLoop = useCallback(() => {
        if (engineRef.current && !engineRef.current.isDisposed) {
            engineRef.current.stopRenderLoop()
            renderLoopActiveRef.current = false
            lastRenderTimeRef.current = 0
        }
    }, [])

    // Memoize intersection observer callback
    const handleIntersection = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const isVisible = entries[0].isIntersecting
            if (!isVisible) {
                stopRenderLoop()
            } else {
                startRenderLoop()
            }
        },
        [startRenderLoop, stopRenderLoop],
    )

    // Memoize visibility change handler
    const handleVisibilityChange = useCallback(() => {
        if (document.hidden) {
            stopRenderLoop()
        } else {
            startRenderLoop()
        }
    }, [startRenderLoop, stopRenderLoop])

    // Setup visibility and intersection observers
    useEffect(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange)

        if (canvasRef.current) {
            observerRef.current = new IntersectionObserver(handleIntersection, observerOptions)
            observerRef.current.observe(canvasRef.current)
        }

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
            stopRenderLoop()
        }
    }, [handleVisibilityChange, handleIntersection, stopRenderLoop, observerOptions])

    const initScene = async () => {
        try {
            const waitForCanvas = async (): Promise<HTMLCanvasElement> => {
                const timeoutMs = 2000
                const start = Date.now()
                while (!canvasRef.current && Date.now() - start < timeoutMs) {
                    await new Promise(r => setTimeout(r, 16))
                }
                if (!canvasRef.current) throw new Error('Canvas reference is not available')
                return canvasRef.current
            }
            const canvas = await waitForCanvas()

            clearAnimationState()

            const { BABYLON, loaders, screenshotTools } = await loadBabylonModules()
            try {
                const { Animation } = BABYLON
                Animation.AllowMatricesInterpolation = true
            } catch (error) {
                console.warn('Failed to enable Babylon matrix interpolation', error)
            }
            const gltfModule = loaders as typeof import('@babylonjs/loaders') & {
                GLTFFileLoader: { IncrementalLoading: boolean; HomogeneousCoordinates: boolean }
            }

            // Initialize core components with proper error handling
            let sceneData
            try {
                // WebGPU screenshot/capture has been flaky in practice (grey/blank captures + destroyed texture errors).
                // Prefer WebGL when we're in capture mode.
                sceneData = await setupScene(BABYLON as BabylonNamespace, canvas, {
                    disableWebGPU: capturePreview,
                })
            } catch (error) {
                console.error('Error in setupScene:', error)
                throw error
            }

            const { engine, scene } = sceneData
            engineRef.current = engine
            sceneRef.current = scene

            const isStillCurrentScene = () => sceneRef.current === scene

            // Load model only after scene is properly initialized
            engine.clearInternalTexturesCache()

            const loadedModel = await loadModel(gltfModule, file, preferLocalUrl)
            if (!loadedModel) {
                throw new Error('Failed to load model data')
            }

            BABYLON.SceneLoader.ShowLoadingScreen = false
            // Disable IndexedDB offline provider to avoid dev-time DB errors
            try {
                BABYLON.Database && (BABYLON.Database.IDBStorageEnabled = false)
            } catch {}
            BABYLON.SceneLoader.loggingLevel = BABYLON.SceneLoader.NO_LOGGING

            const assetExtension = loadedModel.pluginExtension

            const result = await BABYLON.SceneLoader.ImportMeshAsync(
                null,
                '',
                loadedModel.source,
                scene,
                evt => {
                    if (evt.lengthComputable) {
                        const progress = (evt.loaded / evt.total) * 100
                        loadingScreenRef.current?.updateProgress?.(progress)
                        onPreviewProgress?.(progress)
                    }
                },
                assetExtension,
            )
            loadedModel.dispose?.()
            loadingScreenRef.current?.remove()

            const optimizationData = { culledMeshes: 0, disabledShadows: 0, instancedMeshes: 0 }
            const instancingMap = new Map<string, MeshLike>()

            // scene.meshes contains AbstractMesh but we need Mesh-specific
            // properties (geometry, instances, isMesh). The code guards via
            // typeof checks at runtime so we widen the type to include those.
            type MeshLike = AbstractMesh & {
                isMesh?: () => boolean
                geometry?: { uniqueId?: string; id?: string }
                instances?: unknown[]
                createInstance?: (name: string) => AbstractMesh | null
                _isDisposed?: boolean
            }
            scene.meshes.slice().forEach((mesh: MeshLike) => {
                try {
                    if (!mesh || typeof mesh.isMesh !== 'function' || !mesh.isMesh()) return

                    const hasGeometry = Boolean(mesh.geometry)
                    const hasSkeleton = Boolean(mesh.skeleton)
                    const hasMorphTargets = Boolean(mesh.morphTargetManager)

                    if (hasGeometry && !hasSkeleton && !hasMorphTargets) {
                        const materialId = mesh.material?.id || 'matless'
                        const parentId = mesh.parent?.uniqueId || 'root'
                        const key = `${
                            mesh.geometry?.uniqueId || mesh.geometry?.id || mesh.id
                        }:${materialId}:${parentId}`
                        const source = instancingMap.get(key)

                        const canInstance =
                            mesh.getTotalVertices?.() > 0 &&
                            mesh.instances?.length === 0 &&
                            !mesh.doNotSyncBoundingInfo

                        if (!source) {
                            instancingMap.set(key, mesh)
                        } else if (canInstance && source !== mesh && !source._isDisposed) {
                            try {
                                const instance = source.createInstance(
                                    `${source.name || 'instance'}_${
                                        optimizationData.instancedMeshes
                                    }`,
                                )
                                if (instance) {
                                    if (mesh.parent) instance.parent = mesh.parent

                                    if (mesh.rotationQuaternion) {
                                        instance.rotationQuaternion =
                                            mesh.rotationQuaternion.clone()
                                    } else if (instance.rotationQuaternion) {
                                        instance.rotationQuaternion = null
                                        instance.rotation.copyFrom(
                                            mesh.rotation || BABYLON.Vector3.Zero(),
                                        )
                                    } else if (mesh.rotation) {
                                        instance.rotation.copyFrom(mesh.rotation)
                                    }

                                    instance.position.copyFrom(mesh.position)
                                    instance.scaling.copyFrom(mesh.scaling)
                                    instance.isVisible = mesh.isVisible
                                    instance.isPickable = mesh.isPickable
                                    instance.receiveShadows = mesh.receiveShadows
                                    instance.checkCollisions = mesh.checkCollisions
                                    instance.alwaysSelectAsActiveMesh =
                                        mesh.alwaysSelectAsActiveMesh
                                    if (mesh.metadata) instance.metadata = { ...mesh.metadata }

                                    mesh.dispose(false, true)
                                    optimizationData.instancedMeshes += 1
                                    return
                                }
                            } catch (error) {
                                if (window.logBabylon) {
                                    console.warn('Failed to instance mesh', error)
                                }
                            }
                        }
                    }

                    if (mesh.material && mesh.material.needDepthPrePass) {
                        mesh.material.needDepthPrePass = false
                    }

                    if (!mesh.isVisible || !mesh.isEnabled()) {
                        if (mesh.receiveShadows) {
                            mesh.receiveShadows = false
                            optimizationData.disabledShadows += 1
                        }
                        if (mesh.alwaysSelectAsActiveMesh) {
                            mesh.alwaysSelectAsActiveMesh = false
                            optimizationData.culledMeshes += 1
                        }
                        return
                    }

                    if (mesh.receiveShadows && !mesh.material?.needDepthPrePass) {
                        const size = mesh.getTotalVertices?.() || 0
                        if (size > complexityThresholds.vertex) {
                            mesh.receiveShadows = false
                            optimizationData.disabledShadows += 1
                        }
                    }
                } catch (error) {
                    if (window.logBabylon) {
                        console.warn('Mesh optimization skipped due to error', error)
                    }
                }
            })

            if (window.logBabylon) {
                console.log('Mesh optimization stats', optimizationData)
            }

            // Get world extends for camera framing
            const worldExtends = scene.getWorldExtends((mesh: AbstractMesh) => {
                return mesh.isVisible && mesh.isEnabled()
            })

            // Setup camera with model framing
            const camera = setupCamera(BABYLON, scene, canvasRef.current, {
                worldExtends,
                assetExtension,
            })
            scene.activeCamera = camera

            // Setup lighting
            setupLighting(BABYLON, scene)

            const { totalVertices, totalIndices } = getSceneComplexity(scene)
            const hasSkeletons = Array.isArray(scene.skeletons)
                ? scene.skeletons.some((s: Skeleton) => (s.bones?.length || 0) > 0)
                : false
            const hasParticleSystems = Array.isArray(scene.activeParticleSystems)
                ? scene.activeParticleSystems.length > 0
                : false
            const preferLowResEnv =
                totalVertices > complexityThresholds.vertex ||
                totalIndices > complexityThresholds.index ||
                hasParticleSystems

            if (!capturePreview && !scene.environmentTexture) {
                await setupEnvironment(BABYLON, scene, { preferLowRes: preferLowResEnv })
            }

            const groups = scene.animationGroups || []

            // Respect any environment or tone mapping hints from the GLTF if present
            // @ts-expect-error - gltf is injected by the GLTF loader at runtime, not in ImportMeshAsync return type
            const gltfData = result.gltf
            if (gltfData) {
                const defaultScene = gltfData.scene || 0
                const sceneData = gltfData.scenes?.[defaultScene]
                applyEnvironmentSettingsFromGLTF(scene, sceneData)
            }

            if (isStillCurrentScene()) {
                if (groups.length) {
                    groups.forEach((group, index) => {
                        group.reset()
                        if (index === 0) {
                            group.play(true)
                        } else {
                            group.stop()
                        }
                    })
                }

                const isStaticScene = groups.length === 0 && !hasSkeletons && !hasParticleSystems

                canFreezeActiveMeshesRef.current = isStaticScene

                if (isStaticScene) {
                    scheduleFreezeActiveMeshes()
                } else {
                    clearFreezeTimeout()
                    try {
                        if (scene.isActiveMeshesFrozen) {
                            scene.unfreezeActiveMeshes()
                        }
                    } catch (error) {
                        console.warn('Failed to unfreeze meshes for dynamic scene', error)
                    }
                }
            }

            const schedulePreviewCapture = () => {
                if (!capturePreview || !onPreviewGenerated || previewCapturedRef.current) return
                const idleCallback =
                    window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 100))
                idleCallback(async () => {
                    try {
                        // Ensure the scene is actually ready (meshes + textures) before we screenshot.
                        // We’ve seen “blank/grey” previews when capture happens before env/tex/model is ready.
                        const waitForSceneReadyForPreview = async () => {
                            const timeoutMs = 12_000
                            const start = Date.now()
                            while (Date.now() - start < timeoutMs) {
                                if (scene.isDisposed) return
                                const hasRenderableMesh = (scene.meshes || []).some(
                                    (m: AbstractMesh) => {
                                        try {
                                            if (!m || m.isDisposed?.()) return false
                                            if (m.isVisible === false) return false
                                            const v =
                                                typeof m.getTotalVertices === 'function'
                                                    ? m.getTotalVertices()
                                                    : 0
                                            return Number(v) > 0
                                        } catch {
                                            return false
                                        }
                                    },
                                )
                                const texturesReady = (scene.textures || []).every(t => {
                                    try {
                                        return typeof t?.isReady === 'function' ? t.isReady() : true
                                    } catch {
                                        return true
                                    }
                                })
                                const ready =
                                    typeof scene.isReady === 'function' ? scene.isReady(true) : true
                                if (ready && texturesReady && hasRenderableMesh) return
                                await new Promise(r => setTimeout(r, 125))
                            }
                        }
                        await waitForSceneReadyForPreview()

                        // Wait a couple frames to ensure the scene has rendered at least once
                        await new Promise(requestAnimationFrame)
                        await new Promise(requestAnimationFrame)
                        try {
                            // Force one render right before capture to avoid grabbing a stale frame.
                            scene.render()
                        } catch {}

                        const cam = scene.activeCamera
                        const engine = engineRef.current
                        const canvas = engine?.getRenderingCanvas?.()
                        if (!cam || !engine || !canvas || !screenshotTools) return

                        previewCapturedRef.current = true

                        const rect = canvas.getBoundingClientRect()
                        const dpr = Math.min(window.devicePixelRatio || 1, 2)
                        const maxDim = 800

                        const calcDims = (aspect: PreviewAspect) => {
                            if (!rect) return { width: 640, height: 640 }
                            const rw = rect.width * dpr
                            const rh = rect.height * dpr
                            let targetW = rw
                            let targetH = rh
                            if (aspect === '1x1') {
                                const s = Math.min(rw, rh, maxDim)
                                targetW = s
                                targetH = s
                            } else if (aspect === '16x9') {
                                const w = Math.min(rw, maxDim)
                                targetW = w
                                targetH = Math.round((w * 9) / 16)
                            } else if (aspect === '9x16') {
                                const h = Math.min(rh, maxDim)
                                targetH = h
                                targetW = Math.round((h * 9) / 16)
                            }
                            return {
                                width: Math.max(1, Math.round(targetW)),
                                height: Math.max(1, Math.round(targetH)),
                            }
                        }

                        const aspects: PreviewAspect[] =
                            captureAspects && captureAspects.length ? captureAspects : ['1x1']

                        const resolutionScale = Math.max(
                            1,
                            typeof captureResolution === 'number' && captureResolution > 0
                                ? captureResolution
                                : Math.round(dpr),
                        )

                        const captureOnce = async (w: number, h: number) => {
                            const targetWidth = Math.max(1, Math.round(w))
                            const targetHeight = Math.max(1, Math.round(h))
                            const baseSize = {
                                width: targetWidth,
                                height: targetHeight,
                                precision: Math.max(1, resolutionScale),
                            }
                            const renderTargetSize = (() => ({
                                ...baseSize,
                                finalWidth: targetWidth,
                                finalHeight: targetHeight,
                            }))()

                            const captureWith = (
                                method: 'canvas' | 'renderTarget',
                            ): Promise<string> =>
                                new Promise((resolve, reject) => {
                                    const handle = (dataUrl: string) => {
                                        if (!dataUrl || dataUrl === 'data:,') {
                                            reject(
                                                new Error(`Empty preview data URL from ${method}`),
                                            )
                                            return
                                        }
                                        resolve(dataUrl)
                                    }

                                    try {
                                        if (method === 'renderTarget') {
                                            screenshotTools.CreateScreenshotUsingRenderTarget(
                                                engine,
                                                cam,
                                                renderTargetSize,
                                                handle,
                                                'image/png',
                                            )
                                            return
                                        }

                                        screenshotTools.CreateScreenshot(
                                            engine,
                                            cam,
                                            baseSize,
                                            handle,
                                            'image/png',
                                            false,
                                            undefined,
                                            true,
                                        )
                                    } catch (err) {
                                        reject(err)
                                    }
                                })

                            let lastError: unknown = null
                            const methods: Array<'canvas' | 'renderTarget'> = [
                                'canvas',
                                'renderTarget',
                            ]
                            for (const method of methods) {
                                try {
                                    return await captureWith(method)
                                } catch (error) {
                                    lastError = error
                                }
                            }

                            throw lastError || new Error('Preview capture failed')
                        }

                        for (const aspect of aspects) {
                            const { width, height } = calcDims(aspect)
                            try {
                                const dataUrl = await captureOnce(width, height)
                                try {
                                    const blob = await (await fetch(dataUrl)).blob()
                                    // onPreviewGenerated signature only declares (blob, dataUrl)
                                    // but callers also pass aspect; we widen here to match runtime usage
                                    await (
                                        onPreviewGenerated as (
                                            blob: Blob,
                                            dataUrl: string,
                                            aspect?: PreviewAspect,
                                        ) => void | Promise<void>
                                    )(blob, dataUrl, aspect)
                                } catch (error) {
                                    previewCapturedRef.current = false
                                    console.error('Failed to convert screenshot to blob:', error)
                                    onPreviewFailed?.(error)
                                }
                            } catch (captureError) {
                                previewCapturedRef.current = false
                                console.error('Preview capture failed:', captureError)
                                onPreviewFailed?.(captureError)
                            }
                        }
                    } catch (error) {
                        previewCapturedRef.current = false
                        console.error('Preview capture failed:', error)
                        onPreviewFailed?.(error)
                    }
                })
            }

            // Safely check visibility and start render loop if needed
            const shouldStartImmediately =
                !document.hidden &&
                canvasRef.current &&
                canvasRef.current.getBoundingClientRect().top < window.innerHeight

            if (shouldStartImmediately) {
                startRenderLoop()
                if (groups.length) {
                    requestAnimationFrame(() => scheduleFreezeActiveMeshes())
                }
                schedulePreviewCapture()
            } else {
                scene.executeWhenReady(() => {
                    startRenderLoop()
                    if (groups.length) {
                        requestAnimationFrame(() => scheduleFreezeActiveMeshes())
                    }
                    schedulePreviewCapture()
                })
            }

            // Configure GLTFFileLoader settings
            gltfModule.GLTFFileLoader.IncrementalLoading = false
            gltfModule.GLTFFileLoader.HomogeneousCoordinates = false

            return { engine, scene }
        } catch (error) {
            console.error('Error in initScene:', error)
            throw error
        }
    }

    useEffect(() => {
        let isComponentMounted = true

        const init = async () => {
            loadingScreenRef.current = setupLoadingScreenMemo(canvasRef)

            // Add event listeners
            canvasRef.current?.addEventListener('touchstart', handleTouchStart, { passive: false })
            canvasRef.current?.addEventListener('touchmove', handleTouchMove, { passive: false })
            canvasRef.current?.addEventListener('touchend', handleTouchEnd)
            canvasRef.current?.addEventListener('wheel', preventScroll, { passive: false })
            canvasRef.current?.addEventListener('pointerdown', markInteraction, { passive: true })
            canvasRef.current?.addEventListener('pointermove', handlePointerMove, { passive: true })

            try {
                const { engine, scene } = await initScene()
                if (!isComponentMounted) {
                    // Component unmounted during async operation, clean up and return
                    engine.dispose()
                    return
                }

                engineRef.current = engine
                sceneRef.current = scene

                const resizeListener = () => {
                    if (engine && !engine.isDisposed) {
                        engine.resize()
                    }
                }
                window.addEventListener('resize', resizeListener)

                loadingScreenRef.current?.remove()
            } catch (error) {
                console.error('Error initializing scene:', error)
                onPreviewFailed?.(error)
                if (isComponentMounted && loadingScreenRef.current) {
                    showError(loadingScreenRef.current)
                }
            }
        }

        init()

        return () => {
            isComponentMounted = false
            stopRenderLoop()
            window.removeEventListener('resize', handleResize)

            const currentCanvas = canvasRef.current
            currentCanvas?.removeEventListener('touchstart', handleTouchStart)
            currentCanvas?.removeEventListener('touchmove', handleTouchMove)
            currentCanvas?.removeEventListener('touchend', handleTouchEnd)
            currentCanvas?.removeEventListener('wheel', preventScroll)
            currentCanvas?.removeEventListener('pointerdown', markInteraction)
            currentCanvas?.removeEventListener('pointermove', handlePointerMove)

            if (loadingScreenRef.current?.parentNode) {
                loadingScreenRef.current.remove()
            }

            const currentScene = sceneRef.current
            const currentEngine = engineRef.current

            clearAnimationState()
            clearInteractionTimeout()
            clearFreezeTimeout()
            isUserInteractingRef.current = false
            canFreezeActiveMeshesRef.current = false

            sceneRef.current = null
            engineRef.current = null

            if (currentScene && !currentScene.isDisposed) {
                currentScene.dispose()
            }
            if (currentEngine && !currentEngine.isDisposed) {
                currentEngine.dispose()
            }
        }
    }, [
        file.url,
        file.name,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        preventScroll,
        handlePointerMove,
        handleResize,
        setupLoadingScreenMemo,
        modelLoadConfig,
        stopRenderLoop,
        clearAnimationState,
        markInteraction,
        clearInteractionTimeout,
        clearFreezeTimeout,
        scheduleFreezeActiveMeshes,
    ])

    // We rely on parent (OrdinalsViewer) to ignore clicks inside this container
    // via [data-no-modal-interaction] checks. No need to stop events here.

    return (
        <div
            className={cn(styles.container, className)}
            style={height ? { height } : undefined}
            data-no-modal-interaction
        >
            <canvas ref={canvasRef} className={styles.canvas} />
        </div>
    )
}

const showError = (loadingScreen: LoadingScreenElement) => {
    const errorDiv = document.createElement('div')
    errorDiv.style.color = '#fff'
    errorDiv.style.marginTop = '10px'
    errorDiv.style.padding = '8px 12px'
    errorDiv.style.background = 'rgba(255, 0, 0, 0.7)'
    errorDiv.style.borderRadius = '4px'
    errorDiv.textContent = 'Error loading 3D model'
    loadingScreen.appendChild(errorDiv)
}

// GLTF scene data from the loader is an opaque JSON structure without published types
interface GLTFSceneData {
    extensions?: Record<string, { intensity?: number }>
    materials?: Array<{
        pbrMetallicRoughness?: unknown
        extensions?: Record<string, unknown>
    }>
}

const applyEnvironmentSettingsFromGLTF = (
    scene: BabylonScene,
    sceneData: GLTFSceneData | undefined,
) => {
    const envSettings =
        sceneData?.extensions?.['KHR_lights_punctual'] ||
        sceneData?.extensions?.['KHR_lights_environment']

    if (envSettings) {
        if (window.logBabylon) console.log('Environment settings found:', envSettings)
        if (scene && envSettings.intensity !== undefined) {
            scene.environmentIntensity = envSettings.intensity
        }
    }

    const materials = sceneData?.materials || []
    const hasMetallicRoughness = materials.some(
        mat => mat.pbrMetallicRoughness || mat.extensions?.['KHR_materials_pbrSpecularGlossiness'],
    )

    if (hasMetallicRoughness && scene?.imageProcessingConfiguration) {
        const config = scene.imageProcessingConfiguration
        config.exposure = 1.0
        config.contrast = 1.1
        config.toneMappingEnabled = true
    }
}

const loadBinaryFile = async (
    file: { url: string },
    retries = 5,
    delayMs = 700,
): Promise<LoadedBinaryFile | null> => {
    for (let attempt = 0; attempt < retries; attempt++) {
        try {
            const response = await fetch(file.url)
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            const contentType = response.headers.get('content-type') || ''
            // Accept local payload URLs even if content-type headers are missing.
            const isLocalPayloadUrl = file.url.startsWith('blob:') || file.url.startsWith('data:')
            if (
                !isLocalPayloadUrl &&
                !contentType.includes('model') &&
                !contentType.includes('application/octet-stream') &&
                !contentType.includes('binary/octet-stream')
            ) {
                throw new Error(`Unexpected content-type: ${contentType}`)
            }
            const arrayBuffer = await response.arrayBuffer()
            return { arrayBuffer, contentType }
        } catch (error) {
            if (attempt === retries - 1) {
                console.warn('Error loading file (final attempt):', error)
                return null
            }
            await new Promise(r => setTimeout(r, delayMs))
        }
    }
    return null
}
